import React, { useCallback, useEffect, useState } from "react";

// 이용 기간·안내 메일(2026-10-04) — loginHS 무료 기간(첫 사용부터 1개월, 기존 회원 올해 말까지, DocuAI 유료 구독이면 열림)을
// 회원별로 보고 연장한다. 아래는 안내 메일(가입 3·10일째 미사용 독려 — 마케팅 동의자만 / 만료 7일 전 안내) 대상 미리 보기와 견본 발송.
function authHeaders(json) {
  const token = localStorage.getItem("admin_token");
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  return json ? { ...h, "Content-Type": "application/json" } : h;
}

const MUTED = "var(--audit-muted, #6b7482)";
const LINE = "var(--audit-line, #e3e6ea)";
const BLUE = "#1a56db";

export const STATE_TEXT = {
  paid: "DocuAI 구독",
  extended: "관리자 연장",
  legacy: "기존 회원 무료",
  trial: "무료 기간",
  not_started: "아직 안 씀",
  expired: "끝남",
  off: "잠금 꺼짐",
  unknown: "조회 실패(열림)",
};

const KIND_TEXT = { nudge1: "독려 1(가입 3일째)", nudge2: "독려 2(가입 10일째, 마지막)", expiry: "만료 7일 전 안내" };

function day(v) {
  return v ? String(v).slice(0, 10) : "—";
}

function StatusCell({ s }) {
  if (!s) return <span style={{ color: MUTED }}>—</span>;
  return (
    <span>
      <b style={{ color: s.allowed ? "#1f2937" : MUTED }}>{STATE_TEXT[s.state] || s.state}</b>
      {s.until && <span style={{ color: MUTED }}> · {s.until}까지{s.days_left != null ? ` (${s.days_left}일)` : ""}</span>}
    </span>
  );
}

export default function AdminEntitlement({ API_BASE_URL = "", fetchImpl = fetch }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [pick, setPick] = useState({ group_id: "", user_id: "" });
  const [detail, setDetail] = useState(null);
  const [ext, setExt] = useState({ days: "30", until: "", reason: "" });
  const [msg, setMsg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [sample, setSample] = useState({ kind: "nudge1", to: "" });
  const [busy, setBusy] = useState(null);

  const call = useCallback(async (path, opts = {}) => {
    const r = await fetchImpl(`${API_BASE_URL}${path}`, { ...opts, headers: authHeaders(!!opts.body) });
    if (r.status === 401 || r.status === 403) throw new Error("관리자 로그인이 필요합니다(토큰 만료일 수 있습니다)");
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.message || j.error || `오류 ${r.status}`);
    return j;
  }, [API_BASE_URL, fetchImpl]);

  const loadList = useCallback(async (query) => {
    try {
      const j = await call(`/api/onto/admin/entitlement?limit=100${query ? `&q=${encodeURIComponent(query)}` : ""}`);
      setList(j); setError(null);
    } catch (e) { setError(e.message); }
  }, [call]);

  const loadPlan = useCallback(async () => {
    try { setPlan(await call("/api/onto/admin/entitlement/mail/preview")); } catch (e) { setError(e.message); }
  }, [call]);

  useEffect(() => { loadList(""); loadPlan(); }, [loadList, loadPlan]);

  const openUser = async (g, u) => {
    setPick({ group_id: g, user_id: u }); setMsg(null);
    try { setDetail(await call(`/api/onto/admin/entitlement/user?group_id=${encodeURIComponent(g)}&user_id=${encodeURIComponent(u)}`)); }
    catch (e) { setMsg(e.message); }
  };

  const doExtend = async (clear) => {
    if (!pick.group_id || !pick.user_id) { setMsg("그룹 ID·사용자 ID 를 넣어 주세요."); return; }
    if (!ext.reason.trim()) { setMsg("연장 사유를 적어 주세요."); return; }
    const body = { group_id: pick.group_id, user_id: pick.user_id, reason: ext.reason.trim() };
    if (clear) body.until = null;
    else if (ext.until) body.until = ext.until;
    else body.days = Number(ext.days);
    setBusy("extend"); setMsg(null);
    try {
      const j = await call("/api/onto/admin/entitlement/extend", { method: "POST", body: JSON.stringify(body) });
      setDetail({ ...pick, status: j.status, history: j.history });
      setMsg(clear ? "연장을 지웠습니다." : `연장했습니다 — ${j.status?.until || ""}까지.`);
      loadList(q);
    } catch (e) { setMsg(e.message); } finally { setBusy(null); }
  };

  const sendSample = async () => {
    if (!sample.to.includes("@")) { setMsg("견본을 받을 메일 주소를 넣어 주세요."); return; }
    setBusy("sample"); setMsg(null);
    try {
      const j = await call("/api/onto/admin/entitlement/mail/sample", { method: "POST", body: JSON.stringify(sample) });
      setMsg(j.ok ? `견본을 보냈습니다 — ${sample.to}` : `보내지 못했습니다: ${j.message}`);
    } catch (e) { setMsg(e.message); } finally { setBusy(null); }
  };

  const runNow = async () => {
    if (!plan?.count) return;
    if (!window.confirm(`오늘 대상 ${plan.count}명에게 지금 메일을 보냅니다. 보내시겠습니까?`)) return;
    setBusy("run"); setMsg(null);
    try {
      const j = await call("/api/onto/admin/entitlement/mail/run", { method: "POST" });
      setMsg(`보냄 ${j.sent}통 · 실패 ${j.failed}통`);
      loadPlan(); loadList(q);
    } catch (e) { setMsg(e.message); } finally { setBusy(null); }
  };

  const input = { padding: "6px 8px", border: `1px solid ${LINE}`, borderRadius: 6, fontSize: 13 };
  const btn = (primary) => ({ padding: "6px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
    border: primary ? 0 : `1px solid ${LINE}`, background: primary ? BLUE : "#fff", color: primary ? "#fff" : "#1f2937" });
  const th = { textAlign: "left", padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED, fontWeight: 500 };
  const td = { padding: "6px 8px", borderBottom: `1px solid ${LINE}` };

  return (
    <div style={{ fontSize: 13, color: "#1f2937", display: "grid", gap: 24 }}>
      <section>
        <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>이용 기간</h3>
        <p style={{ color: MUTED, margin: "0 0 12px" }}>
          무료 기간은 로그인한 첫 사용부터 1개월, 기존 회원은 {list?.legacy_until || "2026-12-31"}까지, DocuAI 유료 구독이면 계속 열립니다.
          끝나면 AI·대량 분류·관세사 이력·문서 인식만 막히고 관세율표·검색은 그대로입니다.
        </p>
        {error && <p role="alert" style={{ color: "#b91c1c" }}>{error}</p>}
        <form onSubmit={(e) => { e.preventDefault(); loadList(q); }} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input aria-label="사용자 찾기" placeholder="사용자 ID·그룹 ID" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...input, width: 240 }} />
          <button type="submit" style={btn(false)}>찾기</button>
        </form>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr><th style={th}>사용자</th><th style={th}>첫 사용</th><th style={th}>무료 기간 끝</th><th style={th}>연장</th><th style={th}>지금 상태</th></tr></thead>
          <tbody>
            {(list?.rows || []).map((r) => (
              <tr key={`${r.group_id}/${r.user_id}`} onClick={() => openUser(r.group_id, r.user_id)} style={{ cursor: "pointer" }}>
                <td style={td}>{r.user_id} <span style={{ color: MUTED }}>{r.group_id}</span></td>
                <td style={td}>{day(r.first_use_at)}</td>
                <td style={td}>{day(r.trial_until)}</td>
                <td style={td}>{day(r.extended_until)}</td>
                <td style={td}><StatusCell s={r.status} /></td>
              </tr>
            ))}
            {list && !(list.rows || []).length && <tr><td style={{ ...td, color: MUTED }} colSpan={5}>아직 기록이 없습니다(첫 사용 때 생깁니다).</td></tr>}
          </tbody>
        </table>

        <div style={{ marginTop: 16, padding: 12, border: `1px solid ${LINE}`, borderRadius: 8, display: "grid", gap: 8 }}>
          <b>회원 한 명 보기·연장</b>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input aria-label="그룹 ID" placeholder="그룹 ID" value={pick.group_id} onChange={(e) => setPick({ ...pick, group_id: e.target.value })} style={{ ...input, width: 140 }} />
            <input aria-label="사용자 ID" placeholder="사용자 ID" value={pick.user_id} onChange={(e) => setPick({ ...pick, user_id: e.target.value })} style={{ ...input, width: 200 }} />
            <button type="button" style={btn(false)} onClick={() => openUser(pick.group_id, pick.user_id)}>보기</button>
          </div>
          {detail && (
            <div>
              지금 상태: <StatusCell s={detail.status} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <label>오늘부터 <input aria-label="연장 일수" type="number" min="1" value={ext.days} onChange={(e) => setExt({ ...ext, days: e.target.value, until: "" })} style={{ ...input, width: 70 }} />일</label>
            <span style={{ color: MUTED }}>또는</span>
            <label>마지막 날 <input aria-label="연장 마지막 날" type="date" value={ext.until} onChange={(e) => setExt({ ...ext, until: e.target.value })} style={input} /></label>
            <input aria-label="연장 사유" placeholder="사유(필수)" value={ext.reason} onChange={(e) => setExt({ ...ext, reason: e.target.value })} style={{ ...input, flex: 1, minWidth: 180 }} />
            <button type="button" style={btn(true)} disabled={busy === "extend"} onClick={() => doExtend(false)}>연장</button>
            <button type="button" style={btn(false)} disabled={busy === "extend"} onClick={() => doExtend(true)}>연장 지우기</button>
          </div>
          {detail?.history?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: MUTED }}>
              {detail.history.map((h, i) => (
                <li key={i}>{day(h.at)} · {h.action === "trial_start" ? "무료 기간 시작" : h.action === "extend" ? "연장" : "연장 지움"}
                  {h.until ? ` · ${h.until}까지` : ""}{h.reason ? ` · ${h.reason}` : ""}{h.by_who ? ` · ${h.by_who}` : ""}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>안내 메일</h3>
        <p style={{ color: MUTED, margin: "0 0 12px" }}>
          매일 10시 — 가입 3·10일째 아직 안 쓴 사람(마케팅 수신 동의자만, 2번까지, 제목 앞 (광고)·수신 거부 링크) · 써 본 사람의 기간이 7일 남았을 때 한 번.
          자동 발송: <b data-testid="mail-enabled">{plan ? (plan.enabled ? "켜짐" : "꺼짐") : "…"}</b>
        </p>
        <b>오늘 보낼 대상 {plan ? `${plan.count}명` : ""}</b>
        <ul style={{ margin: "6px 0 12px", paddingLeft: 18 }}>
          {(plan?.targets || []).map((t, i) => (
            <li key={i}>{KIND_TEXT[t.kind] || t.kind} · {t.user_id} <span style={{ color: MUTED }}>{t.email} · {t.detail}</span></li>
          ))}
          {plan && !plan.count && <li style={{ color: MUTED }}>오늘은 없습니다.</li>}
        </ul>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select aria-label="견본 종류" value={sample.kind} onChange={(e) => setSample({ ...sample, kind: e.target.value })} style={input}>
            {Object.entries(KIND_TEXT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input aria-label="견본 받을 주소" placeholder="견본 받을 메일 주소" value={sample.to} onChange={(e) => setSample({ ...sample, to: e.target.value })} style={{ ...input, width: 240 }} />
          <button type="button" style={btn(false)} disabled={busy === "sample"} onClick={sendSample}>견본 보내기</button>
          <button type="button" style={btn(true)} disabled={busy === "run" || !plan?.count} onClick={runNow}>오늘 대상에게 지금 보내기</button>
        </div>
      </section>

      {msg && <p role="status" style={{ margin: 0 }}>{msg}</p>}
    </div>
  );
}
